import React, { useEffect, useRef, useState } from 'react'
import className from 'classnames'
import DatePicker from 'react-datepicker'
import InputMask from "react-input-mask";
import Header from './Header'
import Link from '../Typography/Link/Link'
import { format, isValid, parse, parseISO } from 'date-fns'
import getYear from "date-fns/getYear";
import { v4 } from 'uuid';
import { months, validDateRegex, inputMaskDefaultValues, getMaskValue } from './helpers'
import 'react-date-range/dist/styles.css' // main css file
import 'react-datepicker/dist/react-datepicker.css'
import 'react-date-range/dist/theme/default.css' // theme css file
import './Datepicker.css'

import { getMinDate, getMaxDate, getInitialDate, getStartDate, getEndDate, getFormat } from '../DateTime/helper'
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup';
import Button from '../Button/Button';
import Icon from '../Icon2/Icon';

interface IDatepicker {
  initialDate?: any
  formatType?: 'yyyy-mm-dd' | 'mm dd yyyy' | 'dd mmm yyyy' | 'mmmm dd, yyyy' | string
  startDate?: Date
  endDate?: Date
  onChange?: (data?: any, from?: string) => void
  viewType?: string
  noBorder?: boolean
  selectsStart?: boolean
  selectsEnd?: boolean
  closeModal?: () => void
  numberOfYearsToRender?: number
  version?: "v2"
  testId?: string
  cancelCtaTestId?: string
  doneCtaTestId?: string
  inputMaskProps?: {
    characterSeperator?: string
    hasInputMask?: boolean
    onKeyDown?: Function
    disabled?: boolean
    inputMaskTestId?: string
    maskType?: string
    maskPlaceholder?: string
    placeholder?: string
    classname?: string
    value?: string
    maskRegex?: string
    hasErrorCB?: Function
    showErrorBorder?: boolean
    inputMaskWidth?: number
  }
  datePickerHeadersProps?: Object
  withFooter?: boolean
  withCurrentDate?: boolean,
  datePickerRestProps?: any
  enablePropagationOnchange?: boolean
  closeOnOverlayClick?: boolean
  errorBorder?: boolean,
  renderForVariant?: boolean
  isDateTimeCombo?: boolean
  onCancelClick?: () => void
}

const renderDayContents = (_, date) => {
  return (
    <div data-test-id="cs-calender-date" className="custom-day-date">
      <span className="custom-day-date__text">{date.getDate()}</span>
    </div>
  )
}

const renderWeekDayContents = (nameOfDay) => {
  return (
    <div className="custom-week-day-name">
      <span className="custom-week-day__text">{nameOfDay.substr(0, 1)}</span>
    </div>
  )
}

const renderDatePickerInputSuffix = () => {
  return (<span className="TimeInput__suffix__wrapper">
    <Icon
      className={`ml-16 Dropdown__chevron`}
      icon="CalendarBlank"
      version='v2'
      size="medium"
    />
  </span>)
}

const CustomInputMaskRender = React.forwardRef(({ value, ...rest }: any, calendarRef: any) => {
  const {
    dateDisabled,
    inputMaskTestId,
    maskType,
    maskPlaceholder,
    inputMaskplaceholder,
    classname,
    inputMaskValue,
    customInputMaskChange,
    isCalenderOpen,
    setIsCalenderOpen,
    invalidDateInput,
    inputMaskWidth,
    errorBorder
  } = rest
  const openCalenderPicker = (e: any) => {
    e.stopPropagation()
    setIsCalenderOpen(!isCalenderOpen)
  }
  const onKeyDownHandlerInput = (e: any) => {
    if (e.key === "Enter" || e.key === " ") {
      openCalenderPicker(e)
    }
  }

  return (<div
    className={`Datepicker-inputmask ${(invalidDateInput || errorBorder) ? "Datepicker-inputmask--negative" : ""} ${dateDisabled ? "Datepicker-inputmask--disabled" : ""}`}
    style={inputMaskWidth ? {
      width: `${inputMaskWidth}px`
    } : {}}
    onClick={openCalenderPicker}
    onKeyDown={onKeyDownHandlerInput}
  >
    <InputMask
      tabIndex={dateDisabled ? -1 : 0}
      data-test-id={inputMaskTestId}
      mask={maskType === "99-99-9999" ? getMaskValue(inputMaskValue) : maskType}
      maskPlaceholder={maskType === "99-99-9999" ? ' ' : maskPlaceholder}
      // mask={maskType}
      // maskPlaceholder={maskPlaceholder}
      // mask={getMaskValue(inputMaskValue)}
      // maskPlaceholder={' '}
      placeholder={inputMaskplaceholder}
      className={classname || ""}
      // value={inputMaskValue}
      value={inputMaskValue ? inputMaskValue?.trim() : inputMaskValue}
      onChange={customInputMaskChange}
    />
    {renderDatePickerInputSuffix()}
  </div>)
})

const CalenderFooter = (props: any) => {
  const { cancelCtaTestId, doneCtaTestId, cancelHandler, doneHandler } = props

  return (<div className="DatePicker__actions">
    <ButtonGroup>
      <Button testId={`${cancelCtaTestId ? cancelCtaTestId : 'cs-date-picker-cancel'}`} buttonType="tertiary" version='v2' onClick={cancelHandler}>
        Cancel
      </Button>
      <Button testId={`${doneCtaTestId ? doneCtaTestId : 'cs-date-picker-apply'}`} size="regular" buttonType="primary" version='v2' onClick={doneHandler} >
        Apply
      </Button>
    </ButtonGroup>
  </div>)
}

const Datepicker = (props: IDatepicker) => {

  const {
    initialDate,
    formatType,
    startDate,
    endDate,
    onChange,
    closeModal,
    viewType,
    noBorder,
    selectsStart = false,
    selectsEnd = false,
    numberOfYearsToRender = 9,
    version,
    testId = "cs-datepicker",
    cancelCtaTestId,
    doneCtaTestId,
    inputMaskProps,
    withFooter = true,
    withCurrentDate = true,
    datePickerHeadersProps = {},
    datePickerRestProps = {},
    enablePropagationOnchange = {},
    closeOnOverlayClick = true,
    errorBorder = false,
    isDateTimeCombo,
    renderForVariant = false,
    onCancelClick
  } = props

  const {
    hasInputMask = true,
    characterSeperator,
    disabled = false,
    inputMaskTestId = "cs-date-input-mask",
    maskType = "99-99-9999",
    maskPlaceholder = "mm-dd-yyyy",
    placeholder = "mm-dd-yyyy",
    classname = "",
    maskRegex,
    hasErrorCB,
    showErrorBorder = true,
    inputMaskWidth
  } = { ...inputMaskDefaultValues, ...inputMaskProps }

  const type = formatType ? getFormat(formatType, characterSeperator || undefined) : 'yyyy-MM-dd'
  const sDate = startDate ? getStartDate(startDate) : new Date('01/13/1800')
  const eDate = endDate ? getEndDate(endDate, sDate) : new Date('01/13/3022')
  const [date, setDate] = useState(initialDate ? getInitialDate(initialDate, formatType) : '')
  let validDate = ''
  if (date && isValid(date)) validDate = format(new Date(date), type)
  const [_, setValue] = useState(validDate)
  const [calenderView, setCalenderView] = useState("date")
  const [invalidDateInput, setInvalidDateInput] = useState(false)
  const calenderHeaderRef = useRef(null);
  const datePickerRef = useRef(null);
  const [isCalenderOpen, setIsCalenderOpen] = useState(false)
  const [inputMaskValue, setInputMaskValue] = useState((date && isValid(date)) ? format(new Date(date), type) : '')
  const rootDivUid = `root-portal-picker-${v4().split('-').join('')}`

  const calendarClickHandler = (item: any) => {
    let value = ''
    if (viewType === 'time' || viewType === 'datetime')
      value = new Date(item).getTime() ? format(item, 'yyyy-MM-dd') : item
    else value = new Date(item).getTime() ? format(item, type) : item
    setValue(value)
    if (version !== "v2" && invalidDateInput) setInvalidDateInput(false)
    if (version !== "v2" || !withFooter) {
      if (onChange) onChange(value)
      if (hasInputMask) setInputMaskValue(value?.split(" ").join('-'))
    }
    if (viewType !== 'datetime' && closeModal) {
      closeModal()
    }

    if (version !== "v2") setDate(item)
  }

  const renderMonthContents = (month, shortMonth, longMonth, day) => {
    const monthClickHandler = (e: any) => {
      e.stopPropagation()
      // if (enablePropagationOnchange) e.stopPropagation();
      let updated_value = _ ? new Date(new Date(_).setMonth(months.indexOf(longMonth))) : new Date(new Date().setMonth(months.indexOf(longMonth)))
      datePickerRef?.current?.setSelected(updated_value)
      calendarClickHandler(updated_value)
      setCalenderView("date")
    }
    return (
      <div data-test-id="cs-calender-month" className="custom-day-date" onClick={monthClickHandler}>
        <span className="custom-day-date__text">{shortMonth}</span>
      </div>
    );
  }

  const renderYearContents = (year) => {
    const yearClickHandler = (e: any) => {
      e.stopPropagation()
      // if (enablePropagationOnchange) e.stopPropagation();
      let updated_value = _ ? new Date(new Date(_).setFullYear(year)) : new Date(new Date().setFullYear(year))
      datePickerRef?.current?.setSelected(updated_value)
      calendarClickHandler(updated_value)
      setCalenderView("month")
    }
    return (
      <div data-test-id="cs-calender-date-year" className="custom-day-date" onClick={yearClickHandler}>
        <span className="custom-day-date__text">{year}</span>
      </div>
    )
  }

  const renderCalenderHeader = (headerProps) => {
    if (!calenderHeaderRef?.current && version === "v2") {
      calenderHeaderRef.current = {
        changeYear: headerProps.changeYear,
        changeMonth: headerProps.changeMonth
      }
    }
    return (<Header
      {...headerProps}
      maxDate={maxDate}
      minDate={minDate}
      setDate={setDate}
      version={version}
      calenderView={calenderView}
      numberOfYearsToRender={numberOfYearsToRender}
      setCalenderView={setCalenderView}
      {...datePickerHeadersProps}
    />)
  }

  const customInputMaskChange = (e: any) => {
    if (e.target.value) {
      let dateValue = e.target.value;
      const calenderWithRange = checkForRangeValidityInput(sDate, eDate, dateValue)
      if (dateValue === maskPlaceholder || dateValue === '  -  -    ' || dateValue === "          ") {
        if (dateValue.replace(/y|m|d/g, '').match(/^[\d-/]+$/) || dateValue === '  -  -    ' || dateValue === "          ") {
          setValue('')
          onChange('')
          setDate('')
          setInputMaskValue('')
          hasErrorCB && hasErrorCB(false)
        }
      } else
        if (((dateValue.match(maskRegex || validDateRegex) !== null) && calenderWithRange) || dateValue === '') {
          if (navigator?.userAgent?.indexOf('Safari') != -1 && navigator?.userAgent?.indexOf('Chrome') == -1) {
            dateValue = dateValue.replaceAll('-', '/')
          }
          const gmtFormat: any = new Date(dateValue) || dateValue
          let updatedDateValue: any = new Date(gmtFormat).getTime() ? format(gmtFormat, type) : gmtFormat
          calenderHeaderRef?.current?.changeMonth(months.indexOf(format(gmtFormat, 'MMMM')))
          calenderHeaderRef?.current?.changeYear(getYear(gmtFormat))
          setValue(updatedDateValue)
          onChange(updatedDateValue)
          if (invalidDateInput && showErrorBorder) setInvalidDateInput(false)
          hasErrorCB && hasErrorCB(false)
          setInputMaskValue(dateValue)
          setDate(gmtFormat)
          datePickerRef?.current?.setPreSelection(gmtFormat)
          if (calenderView !== "date") setCalenderView("date")
        } else {
          if (!invalidDateInput && showErrorBorder) {
            setInvalidDateInput(true)
          }
          hasErrorCB && hasErrorCB(true)
          setInputMaskValue(dateValue)
        }
    }
  }

  const checkForRangeValidityInput = (start, end, current) => {
    let hasRangeInCalender: boolean = true
    if ((start || end)) {
      let startDate: any = start ? start : getMinDate(date)
      let endDate: any = end ? end : getMaxDate(date)
      let currentDate: any = new Date(current)
      if (startDate && endDate && isValid(new Date(current))) {
        hasRangeInCalender = currentDate >= startDate && currentDate <= endDate
      }
    }
    return hasRangeInCalender
  }

  const RenderCurrentDateElement = () => {
    const currentDateSelectHandler = () => { if (calenderView !== "date") setCalenderView("date") }
    return (<Link data-test-id={"cs-datepicker-current-date-link"} cbOnClick={currentDateSelectHandler} fontSize={'regular'} fontWeight={'semi-bold'} underline={true} underLineType="solid">Use Current Date</Link>)
  }

  const calenderOverlayClickHandler = () => {
    if (calenderView !== "date") setCalenderView("date")
    if (closeOnOverlayClick) setIsCalenderOpen(false)
  }

  const calenderCancelHandler = () => {
    if (date) {
      let resetValue = format(new Date(date), type);
      setValue(resetValue);
      calenderHeaderRef?.current?.changeMonth(months.indexOf(format(new Date(date), 'MMMM')))
      calenderHeaderRef?.current?.changeYear(getYear(new Date(date)))
    }
    if (calenderView !== "date") setCalenderView("date")
    setIsCalenderOpen(false)
    if (version === 'v2' && !hasInputMask) {
      if (onCancelClick) onCancelClick();
    }
  }

  const calenderApplyHandler = () => {
    if (_) {
      let parsedDate = parseISO(_);
      if (!isValid(parsedDate)) parsedDate = new Date(_)
      let updatedDateValue: any = format(parsedDate, type)
      onChange(updatedDateValue)
      setDate(parsedDate);
      if (invalidDateInput && showErrorBorder) setInvalidDateInput(false)
      hasErrorCB && hasErrorCB(false)
      if (version === "v2" && hasInputMask) setInputMaskValue(_);
    }
    if (calenderView !== "date") setCalenderView("date")
    setIsCalenderOpen(false)
  }

  useEffect(() => {
    if (version === 'v2' && datePickerRestProps?.ref?.current) {
      datePickerRestProps.ref.current.calenderViewType = calenderView;
      datePickerRestProps.ref.current.setCalenderView = setCalenderView;
    }
  }, [calenderView])

  useEffect(() => {
    if (renderForVariant) {
      let resetDate = initialDate ? getInitialDate(initialDate, formatType) : ''
      setDate(resetDate)
      let validDate = ''
      if (resetDate && isValid(resetDate)) validDate = format(new Date(resetDate), type)
      setValue(validDate)
      setInputMaskValue((resetDate && isValid(resetDate)) ? format(new Date(resetDate), type) : '')
    }
  }, [initialDate])

  let minDate: any = sDate ? sDate : getMinDate(date)
  let maxDate: any = eDate ? eDate : getMaxDate(date)
  let dt: any = date || null
  let datePickerProps: any = {}
  if (version === "v2") {
    if (_ && isDateTimeCombo) {
      dt = parse(_, type, new Date()) || null;
    } else if (_) {
      dt = parseISO(_)
      if (!isValid(dt)) dt = new Date(_);
    }
    if ((!startDate || !endDate) && withCurrentDate) datePickerProps.todayButton = <RenderCurrentDateElement />
    if (datePickerRestProps) datePickerProps = { ...datePickerProps, ...datePickerRestProps }
  }

  return (
    <>
      <div
        data-test-id={testId}
        className={className('Datepicker', {
          'no-border': noBorder,
          'Datepicker__date-time': viewType === 'datetime',
          'Datepicker__v2': version === "v2"
        })}>
        {version === 'v2' && <div id={rootDivUid}></div>}
        <DatePicker
          ref={datePickerRef}
          inline={version === 'v2' ? !hasInputMask : true}
          renderCustomHeader={renderCalenderHeader}
          renderDayContents={renderDayContents}
          renderYearContent={renderYearContents}
          renderMonthContent={renderMonthContents}
          showYearPicker={calenderView === "year"}
          showMonthYearPicker={calenderView === "month"}
          yearItemNumber={numberOfYearsToRender || 9}
          minDate={minDate}
          maxDate={maxDate}
          onChange={calendarClickHandler}
          selected={dt}
          adjustDateOnChange={false}
          selectsStart={calenderView === "date" && selectsStart}
          selectsEnd={calenderView === "date" && selectsEnd}
          formatWeekDay={renderWeekDayContents}
          onClickOutside={calenderOverlayClickHandler}
          customInput={version === "v2" && hasInputMask ? <CustomInputMaskRender
            dateDisabled={disabled}
            inputMaskTestId={inputMaskTestId}
            maskType={maskType}
            maskPlaceholder={maskPlaceholder}
            inputMaskplaceholder={placeholder}
            classname={classname || ""}
            inputMaskValue={inputMaskValue}
            customInputMaskChange={customInputMaskChange}
            isCalenderOpen={isCalenderOpen}
            setIsCalenderOpen={setIsCalenderOpen}
            invalidDateInput={(showErrorBorder && invalidDateInput)}
            inputMaskWidth={inputMaskWidth}
            errorBorder={errorBorder}
          /> : <></>}
          showPopperArrow={false}
          open={isCalenderOpen}
          portalId={rootDivUid}
          {...datePickerProps}
        >
          {version === "v2" && withFooter && (
            <CalenderFooter
              cancelCtaTestId={cancelCtaTestId}
              doneCtaTestId={doneCtaTestId}
              cancelHandler={calenderCancelHandler}
              doneHandler={calenderApplyHandler}
            />
          )}
        </DatePicker>
      </div>
    </>
  )
}

export default Datepicker
